import { Dropdown, Menu } from 'antd';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { COMPANY_PHONE } from '../../constants';
import { useCurrentUser } from '../../hooks';
import { baseConfig, profileRoutes } from '../../routes';
import { logOut } from '../../utils/logOut';
import { StyledDropdownItem, StyledDropdownItemText, StyledDropdownMenu } from '../Navbar/Navbar.styles';
import { Button, IconButton } from '../ui/Button';
import Icon, { IconTypes } from '../ui/Icon/Icon';
import { Search } from '../ui/Search';
import { theme } from '../ui/themes/default';
import { Text } from '../ui/Typography';
import {
  CloseIconWrapper,
  EmergencySection,
  IconWrapper,
  MenuLabel,
  MobileNavbarActions,
  MobileNavbarAvatar,
  MobileNavbarLogo,
  MobileNavbarWrapper,
  PhoneLink,
  PhoneSection,
  ProfileMenuItem,
  ProfileStyledLink,
  ProfileStyledMenu,
  StyledDrawer,
  StyledLink,
  StyledMenu,
  StyledMenuItem,
  UserInfo,
  UserSection,
} from './MobileNavbar.styles';

interface Props {
  emergencyCallClick: () => void;
}

export function MobileNavbar({ emergencyCallClick }: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchFieldOpen, setIsSearchFieldOpen] = useState(false);
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const currentUser = useCurrentUser();

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
    setProfileMenuOpen(false);
    setIsSearchFieldOpen(false);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const openSearchField = useCallback(() => {
    if (isProfileMenuOpen) return;
    setIsSearchFieldOpen(true);
  }, [isProfileMenuOpen]);

  const closeSearchField = useCallback(() => {
    setIsSearchFieldOpen(false);
  }, []);

  const openProfileMenu = useCallback(() => {
    if (isSearchFieldOpen) return;

    setProfileMenuOpen(true);
  }, [isSearchFieldOpen]);

  const closeProfileMenu = useCallback(() => {
    setProfileMenuOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    logOut();
  }, []);
  const menu = (
    <StyledDropdownMenu>
      <Menu>
        <Menu.Item>
          <PhoneSection href={`tel:${COMPANY_PHONE}`} onClick={emergencyCallClick}>
            <Icon icon={IconTypes.Phone} />
            <StyledDropdownItem>
              <StyledDropdownItemText>
                <Text variant="description" color="secondary">
                  Emergency call
                </Text>
              </StyledDropdownItemText>
              <PhoneLink href={`tel:${COMPANY_PHONE}`} onClick={emergencyCallClick}>
                <Text variant="bodyMedium">{COMPANY_PHONE}</Text>
              </PhoneLink>
            </StyledDropdownItem>
          </PhoneSection>
        </Menu.Item>
        {/* <Menu.Item>
          <PhoneSection>
            <Icon icon={IconTypes.Pen} width={15} height={15} />
            <StyledDropdownItem>
              <Text variant="bodyMedium">Report your loss</Text>
            </StyledDropdownItem>
            <Icon icon={IconTypes.AngleRight} fillColor="#E6E6F0" />
          </PhoneSection>
        </Menu.Item> */}
      </Menu>
    </StyledDropdownMenu>
  );

  return (
    <MobileNavbarWrapper>
      <MobileNavbarLogo />
      <MobileNavbarActions>
        {isMenuOpen ? (
          <CloseIconWrapper onClick={closeMenu}>
            <Icon icon={IconTypes.Cross} fillColor={theme.secondary} width={9} height={9} />
          </CloseIconWrapper>
        ) : (
          <>
            <IconButton
              round
              outlined
              buttonSize="medium"
              icon={<Icon icon={IconTypes.Search} fillColor={theme.secondary} width={15} height={15} />}
              onClick={openSearchField}
            />
            <MobileNavbarAvatar onClick={openProfileMenu} imageUrl={currentUser?.image} />
            <IconButton
              round
              buttonSize="medium"
              icon={<Icon icon={IconTypes.Bars} fillColor={theme.secondary} width={15} height={15} />}
              onClick={openMenu}
            />
          </>
        )}
      </MobileNavbarActions>
      <StyledDrawer
        placement="right"
        open={isMenuOpen}
        height="100%"
        style={{ top: 70 }}
        onClose={closeMenu}
        destroyOnClose
        mask={false}
        contentWrapperStyle={{ width: '100% !important', left: 0 }}
      >
        <StyledMenu selectedKeys={[location.pathname]}>
          {baseConfig
            .filter((item) => item.access?.find((role) => role === currentUser?.role))
            .map((item) => (
              <StyledMenuItem key={item.path} active={item.path === location.pathname} onClick={closeMenu}>
                <StyledLink to={item.path}>
                  <IconWrapper>
                    <Icon
                      icon={item.icon}
                      fillColor={item.path === location.pathname ? theme.primary : theme.secondary}
                    />
                  </IconWrapper>
                  <MenuLabel>{item.label}</MenuLabel>
                </StyledLink>
              </StyledMenuItem>
            ))}
        </StyledMenu>
        <EmergencySection>
          <Dropdown overlay={menu} placement="top" trigger={['click']}>
            <Button
              variant="emergency"
              buttonSize="large"
              leftIcon={<Icon icon={IconTypes.EmergencyRequest} fillColor={theme.white} />}
              rightIcon={<Icon icon={IconTypes.AngleDown} fillColor={theme.white} />}
              fullWidth
            >
              Request Emergency Service
            </Button>
          </Dropdown>
        </EmergencySection>
      </StyledDrawer>

      <StyledDrawer
        placement="top"
        open={isSearchFieldOpen}
        getContainer=".ant-layout-content"
        height="auto"
        style={{ top: 0, position: 'absolute' }}
        onClose={closeSearchField}
        destroyOnClose
      >
        <div style={{ padding: '15px 0' }}>
          <Search />
        </div>
      </StyledDrawer>

      <StyledDrawer
        placement="top"
        open={isProfileMenuOpen}
        getContainer=".ant-layout-content"
        height={315}
        style={{ top: 20, position: 'absolute' }}
        onClose={closeProfileMenu}
        destroyOnClose
      >
        <ProfileStyledMenu>
          <UserSection>
            <MobileNavbarAvatar onClick={openProfileMenu} expanded />
            <UserInfo>
              <Text variant="bodySemibold">{`${currentUser?.profile?.firstName} ${currentUser?.profile?.lastName}`}</Text>
              <Text color="secondary" variant="description">
                {currentUser?.role}
              </Text>
            </UserInfo>
          </UserSection>
          {profileRoutes.map((item) => (
            <ProfileMenuItem key={item.path}>
              <ProfileStyledLink to={item.path}>
                <Icon icon={item.icon} fillColor={theme.secondary} width={20} />
                <Text>{item.label}</Text>
              </ProfileStyledLink>
            </ProfileMenuItem>
          ))}
          <ProfileMenuItem onClick={handleLogout}>
            <Text>Log out</Text>
          </ProfileMenuItem>
        </ProfileStyledMenu>
      </StyledDrawer>
    </MobileNavbarWrapper>
  );
}
